import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { switchMap, map, catchError, withLatestFrom } from 'rxjs/operators';

import { TagsService } from '../../../core/services/tags.service';
import * as TagsActions from './tags.actions';
import { selectTagsMeta, selectTagsLastQuery } from './tags.selectors';
import { DEFAULT_TAGS_CURRENT_PAGE, DEFAULT_TAGS_PER_PAGE } from '../models/tag.model';

@Injectable()
export class TagsEffects {
  private actions$ = inject(Actions);
  private tagsService = inject(TagsService);
  private store = inject(Store);

  loadTags$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsActions.loadTags),
      switchMap((action) =>
        this.tagsService
          .getTagsList({
            page: action.page ?? DEFAULT_TAGS_CURRENT_PAGE,
            per_page: action.perPage ?? DEFAULT_TAGS_PER_PAGE,
            search: action.search ?? '',
            sort: action.sort ?? 'created_at',
            sort_direction: action.sortDirection ?? 'desc',
            filters: action.filters,
            filter_link: action.filterLink,
          })
          .pipe(
            map((response) =>
              TagsActions.loadTagsSuccess({
                tags: response.data,
                meta: response.meta,
              })
            ),
            catchError((error) =>
              of(TagsActions.loadTagsFailure({ error: error?.message ?? 'Failed to load tags' }))
            )
          )
      )
    )
  );

  submitTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsActions.submitTag),
      switchMap((action) => {
        const { payload, isEditMode } = action;
        const request$ = isEditMode && payload.uuid
          ? this.tagsService.updateTag(payload.uuid, payload)
          : this.tagsService.createTag(payload);

        return request$.pipe(
          map(() => TagsActions.submitTagSuccess()),
          catchError((error) =>
            of(TagsActions.submitTagFailure({ error: error?.message ?? 'Failed to save tag' }))
          )
        );
      })
    )
  );

  deleteTag$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsActions.deleteTag),
      switchMap((action) =>
        this.tagsService.deleteTag(action.tagId).pipe(
          map(() => TagsActions.deleteTagSuccess()),
          catchError((error) =>
            of(TagsActions.deleteTagFailure({ error: error?.message ?? 'Failed to delete tag' }))
          )
        )
      )
    )
  );

  reloadAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TagsActions.submitTagSuccess, TagsActions.deleteTagSuccess),
      withLatestFrom(
        this.store.select(selectTagsMeta),
        this.store.select(selectTagsLastQuery)
      ),
      map(([, meta, lastQuery]) =>
        TagsActions.loadTags({
          page: meta?.currentPage ?? DEFAULT_TAGS_CURRENT_PAGE,
          perPage: meta?.perPage ?? DEFAULT_TAGS_PER_PAGE,
          search: lastQuery.search,
          filters: lastQuery.filters,
          filterLink: lastQuery.filterLink,
          sort: lastQuery.sort,
          sortDirection: lastQuery.sortDirection,
        })
      )
    )
  );
}

